import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Alert,
  Autocomplete,
  Stack,
  Divider,
} from "@mui/material";
import {
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { MRT_SortingState } from "material-react-table";

import {
  useBlockUserMutation,
  useGetUsersQuery,
  useUpdateUserRoleMutation,
} from "../../services/user.query";
import { DefaultPage } from "../../layouts/default-page";
import { Notify } from "../../layouts/notify";
import ConfirmationDialogue from "../../layouts/confirmation-dialogue";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../common/StatusChip";
import { RowActions } from "../common/RowActions";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";
import { useTableState } from "../../hooks/useTableState";

export default function User() {
  const defaultFilters = useMemo(
    () => ({
      search: "",
    }),
    []
  );

  const {
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    hasActiveFilters,
    setPage,
    setSorting: updateTableSorting,
    setFilters,
    resetFilters,
  } = useTableState(defaultFilters, {
    defaultPage: 1,
    defaultPageSize: 100,
    defaultSortBy: "last_name",
    defaultSortOrder: "asc",
  });

  const [draftSearch, setDraftSearch] = useState(filters.search);

  useEffect(() => {
    setDraftSearch(filters.search);
  }, [filters]);

  const sorting = useMemo<MRT_SortingState>(
    () => [{ id: sortBy, desc: sortOrder === "desc" }],
    [sortBy, sortOrder]
  );

  const {
    data: users = [],
    isLoading,
    isFetching,
    isError,
  } = useGetUsersQuery("");

  const [open, setOpen] = useState<boolean>(false);
  const [blockState, setBlockState] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);

  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const [deleteDeputyReport, { isLoading: deleteLoading }] =
    useBlockUserMutation();

  const handleClose = () => {
    setOpen(false);
  };

  const openBlockModal = (row: any, isBlock: boolean) => {
    setBlockState(isBlock);
    setOpen(true);
    setSelectedItem(row);
  };

  const blockUser = async () => {
    const status = blockState ? "BLOCKED" : "VERIFIED";

    let formData: any = {
      user_id: selectedItem.user_id,
      id: selectedItem?.id,
      status: status,
    };
    await deleteDeputyReport(formData);
    setOpen(false);
  };

  const [openRoleModal, setOpenRoleModal] = useState(false);
  const [updateUserRole, { isLoading: updateUserRoleLoading }] = useUpdateUserRoleMutation();

  const handleRoleModalClose = () => {
    setOpenRoleModal(false);
  };

  const roleSchema = Yup.object({
    role: Yup.string().required("Role is required"),
  });

  const handleSearch = () => {
    setFilters({
      search: draftSearch.trim(),
    });
  };

  const handleClear = () => {
    resetFilters();
  };

  const handleEnterSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const filteredData = useMemo(() => {
    let result = users;

    if (filters.search) {
      const keyword = filters.search.toLowerCase();
      result = result.filter((pr: any) => {
        const searchContent = `${pr?.name || ""} ${pr?.last_name || ""} ${pr?.email || ""} ${pr?.phone_number || ""}`.toLowerCase();
        return searchContent.includes(keyword);
      });
    }

    if (sorting.length > 0) {
      const { id, desc } = sorting[0];
      result = [...result].sort((a: any, b: any) => {
        let valA = a[id];
        let valB = b[id];

        if (id === "last_name") {
          valA = `${a.last_name || ""}, ${a.name || ""}`;
          valB = `${b.last_name || ""}, ${b.name || ""}`;
        }

        if (valA < valB) return desc ? 1 : -1;
        if (valA > valB) return desc ? -1 : 1;
        return 0;
      });
    }

    return result;
  }, [users, filters.search, sorting]);

  const totalCount = filteredData.length;
  const paginatedData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, page, pageSize]);

  const handleExportPDF = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Preparing PDF for all ${filteredData.length.toLocaleString()} public users...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Registered Public Users List", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Users: ${filteredData.length}`,
        14,
        22
      );

      const tableColumn = ["#", "Name", "Email Address", "Mobile Number", "Status", "Registered At"];
      const tableRows = filteredData.map((item: any, idx: number) => [
        idx + 1,
        `${item.last_name || ""}, ${item.name || ""}`.trim() || "—",
        item.email || "—",
        item.phone_number || "—",
        item.status === "VERIFIED" ? "ACTIVE" : item.status || "PENDING",
        item.created_at ? format(new Date(item.created_at), "MM/dd/yyyy") : "—",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Public_Users_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${filteredData.length} users to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    if (filteredData.length === 0) {
      Notify("info", "No records found to export.");
      return;
    }

    setIsExporting(true);
    setExportMessage(`Generating Excel spreadsheet for ${filteredData.length.toLocaleString()} public users...`);
    await new Promise((resolve) => setTimeout(resolve, 50));

    try {
      const rows = filteredData.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Last Name": item.last_name || "—",
        "First Name": item.name || "—",
        "Email Address": item.email || "—",
        "Mobile Number": item.phone_number || "—",
        "Status": item.status === "VERIFIED" ? "ACTIVE" : item.status || "PENDING",
        "Registration Date": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "—",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Public Users");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 20 },
        { wch: 20 },
        { wch: 32 },
        { wch: 18 },
        { wch: 14 },
        { wch: 18 },
      ];

      XLSX.writeFile(wb, `MTRCB_Public_Users_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${filteredData.length} users to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "number",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => (
          <Typography>{(page - 1) * pageSize + row.index + 1}</Typography>
        ),
      },
      {
        accessorKey: "last_name",
        header: "User",
        size: 260,
        Cell: ({ row }: any) => {
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <img
                src={
                  row.original.profile_picture ||
                  "https://smrgejlacnpeerpzxezv.supabase.co/storage/v1/object/public/assets/default-user-image.png"
                }
                alt="Profile"
                style={{ width: 35, height: 35, borderRadius: "50%", cursor: "pointer", objectFit: "cover" }}
                onClick={() => window.open(row.original.profile_picture, "_blank")}
              />
              <Typography>
                {row.original.last_name}, {row.original.name}
              </Typography>
            </Box>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 240,
      },
      {
        accessorKey: "phone_number",
        header: "Mobile #",
        size: 160,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 130,
        Cell: ({ row }: any) => {
          const status = row.original.status as string;
          const label = status === "VERIFIED" ? "ACTIVE" : status || "PENDING";
          return (
            <StatusChip
              label={label}
              tone={label === "ACTIVE" ? "success" : label === "BLOCKED" || label === "REJECTED" ? "error" : "neutral"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 190,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const isVerified = row.original.status === "VERIFIED";
          return (
            <RowActions
              actions={[
                {
                  label: "Change Role",
                  onClick: () => {
                    setSelectedItem(row.original);
                    setOpenRoleModal(true);
                  },
                },
                {
                  label: isVerified ? "Block" : "Unblock",
                  tone: isVerified ? "error" : "default",
                  onClick: () => {
                    openBlockModal(row.original, isVerified);
                  },
                },
              ]}
              inline={2}
            />
          );
        },
      },
    ],
    [page, pageSize]
  );

  return (
    <DefaultPage 
      title="Public Users" 
      subTitle="Manage public user accounts and access."
      loading={isLoading}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered public users. Set filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {isError && (
        <Box mb={2.5}>
          <Alert severity="error" sx={{ borderRadius: "6px", fontSize: 13 }}>
            Failed to load public users.
          </Alert>
        </Box>
      )}

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Search User / Email / Phone"
              value={draftSearch}
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={handleEnterSearch}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={handleClear}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(totalCount || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <DataTableFive
        data={paginatedData}
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        columns={columns as any}
        getRowId={(row: any) => row.id}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={(row: any) => {
          setSelectedItem(row.original);
          setOpenRoleModal(true);
        }}
        sorting={sorting}
        onSortingChange={(updater: any) => {
          const next = typeof updater === "function" ? updater(sorting) : updater;
          if (next && next.length > 0) {
            updateTableSorting(next[0].id, next[0].desc ? "desc" : "asc");
          }
        }}
      />

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      <Dialog
        open={openRoleModal}
        onClose={handleRoleModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "6px",
            border: "1px solid #E2E8F0",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18 }}>Assign Role to this User</DialogTitle>
        <Formik
          initialValues={{
            user_id: "",
            role: "",
          }}
          validationSchema={roleSchema}
          onSubmit={async (values, { setSubmitting }) => {
            let formData: any = {
              id: selectedItem.id,
              role: values?.role,
            };
            try {
              await updateUserRole({
                ...formData,
              });
              Notify("info", "Role added Successfully");
            } catch (error) {
              console.error("Error updating role:", error);
            }
            handleRoleModalClose();
            setSubmitting(false);
          }}
        >
          {({ touched, errors }: any) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field name="role">
                      {({ field, form }: any) => (
                        <Autocomplete
                          options={["AMITY", "LEGACY", "MTRCB"]}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Role"
                              variant="outlined"
                              error={touched.role && Boolean(errors.role)}
                              helperText={touched.role && errors.role}
                              sx={{
                                "& .MuiOutlinedInput-root": { borderRadius: "4px" },
                              }}
                            />
                          )}
                          onChange={(event, value) => {
                            form.setFieldTouched(field.name, true);
                            form.setFieldValue(field.name, value || "");
                          }}
                          isOptionEqualToValue={(option: any, value: any) =>
                            option === value
                          }
                        />
                      )}
                    </Field>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button
                  onClick={handleRoleModalClose}
                  sx={{ borderRadius: "4px", textTransform: "none", color: "#64748B" }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={updateUserRoleLoading}
                  sx={{
                    borderRadius: "4px",
                    textTransform: "none",
                    backgroundColor: "#0072BC",
                    "&:hover": { backgroundColor: "#005a96" },
                  }}
                >
                  Save
                </LoadingButton>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      
      <ConfirmationDialogue
        open={open}
        onClose={handleClose}
        onConfirm={blockUser}
        loading={deleteLoading}
        description={`Do you really want to ${blockState ? "Block" : "Unblock"} ${selectedItem?.name ?? "Unknown"}?`}
        title="Confirm Block"
      />
    </DefaultPage>
  );
}
